import { Flex, Box, Switch } from '@chakra-ui/react';
import { LuPlus, LuGripVertical, LuCamera } from 'react-icons/lu';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import type { DragEndEvent } from '@dnd-kit/core';
import type { MenuContentCategory } from './types';
import type { MenuTheme } from '../../api/menus';
import SortableRow from './SortableRow';
import InlineEdit from './InlineEdit';

interface CategoryGridProps {
  categories: MenuContentCategory[];
  theme: MenuTheme;
  hoverBg: string;
  isEditMode: boolean;
  onCategoryClick?: (id: string) => void;
  onCategorySelect: (id: string) => void;
  onAddCategory?: () => void;
  onReorderCategories?: (event: DragEndEvent) => void;
  onQuickEditCategory?: (categoryId: string, updates: { name?: string; is_visible?: boolean }) => void;
  onQuickEditCategoryImage?: (categoryId: string) => void;
}

const CategoryGrid = ({
  categories,
  theme: mt,
  hoverBg,
  isEditMode,
  onCategoryClick,
  onCategorySelect,
  onAddCategory,
  onReorderCategories,
  onQuickEditCategory,
  onQuickEditCategoryImage,
}: CategoryGridProps) => {
  const { t } = useTranslation();
  const isQuickEditMode = !!onQuickEditCategory;

  const renderCard = (cat: MenuContentCategory) => (
    <>
      <Box
        width="100%"
        height="0"
        paddingBottom="100%"
        position="relative"
        overflow="hidden"
        backgroundColor={mt.border_color}
        css={isQuickEditMode ? { '&:hover .quick-edit-cat-image-overlay': { opacity: 1 } } : undefined}
      >
        {cat.image_url ? (
          <img
            src={cat.image_url}
            alt={cat.name}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Flex position="absolute" inset="0" justifyContent="center" alignItems="center">
            <Box width="40px" height="40px" borderRadius="8px" backgroundColor={mt.secondary_text_color} opacity={0.2} />
          </Flex>
        )}
        {isQuickEditMode && onQuickEditCategoryImage && (
          <Flex
            className="quick-edit-cat-image-overlay"
            position="absolute"
            inset="0"
            justifyContent="center"
            alignItems="center"
            backgroundColor="blackAlpha.500"
            opacity={0}
            transition="opacity 0.15s"
            cursor="pointer"
            onClick={(e) => { e.stopPropagation(); onQuickEditCategoryImage(cat.id); }}
          >
            <Flex backgroundColor="blackAlpha.600" borderRadius="full" p="10px" color="white">
              <LuCamera size={20} />
            </Flex>
          </Flex>
        )}
      </Box>
      <Box p="10px" backgroundColor={hoverBg}>
        <Flex alignItems="center" gap="6px">
          {isQuickEditMode && onQuickEditCategory ? (
            <Box flex="1" minWidth={0}>
              <InlineEdit
                value={cat.name}
                onSave={(name) => onQuickEditCategory(cat.id, { name })}
                fontSize="14px"
                fontWeight="600"
                color={mt.text_color}
              />
            </Box>
          ) : (
            <Box fontSize="14px" fontWeight="600" color={mt.text_color}>
              {cat.name}
            </Box>
          )}
          {isEditMode && cat.is_visible === false && (
            <Box fontSize="10px" fontWeight="500" color="#999999" backgroundColor="#F0F0F0" px="6px" py="1px" borderRadius="4px">
              {t('menus.hidden')}
            </Box>
          )}
        </Flex>
        {isQuickEditMode && onQuickEditCategory && (
          <Flex alignItems="center" gap="6px" mt="6px" onClick={(e) => e.stopPropagation()}>
            <Switch.Root size="sm" checked={cat.is_visible !== false} onCheckedChange={(e) => onQuickEditCategory(cat.id, { is_visible: e.checked })}>
              <Switch.HiddenInput />
              <Switch.Control><Switch.Thumb /></Switch.Control>
            </Switch.Root>
            <Box fontSize="11px" fontWeight="500" color="#999999">{t('menus.visible', 'Visible')}</Box>
          </Flex>
        )}
      </Box>
    </>
  );

  if (isEditMode && onReorderCategories) {
    return (
      <Box px="20px" py="16px">
        <DndContext collisionDetection={closestCenter} onDragEnd={onReorderCategories}>
          <SortableContext items={categories.map(c => c.id)} strategy={rectSortingStrategy}>
            <Flex gap="12px" flexWrap="wrap">
              {categories.map((cat) => (
                <SortableRow key={cat.id} id={cat.id}>
                  {({ dragRef, dragStyle, dragHandleProps }) => (
                    <Box
                      ref={dragRef}
                      style={dragStyle}
                      width="calc(50% - 6px)"
                      borderRadius="10px"
                      overflow="hidden"
                      cursor="pointer"
                      position="relative"
                      borderWidth="2px"
                      borderStyle="solid"
                      borderColor="transparent"
                      transition="border-color 0.15s, background-color 0.15s"
                      _hover={{
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                        backgroundColor: 'rgba(59, 130, 246, 0.08)',
                      }}
                      onClick={() => onCategoryClick?.(cat.id)}
                    >
                      <Flex
                        {...dragHandleProps}
                        position="absolute"
                        top="8px"
                        left="8px"
                        zIndex={2}
                        alignItems="center"
                        justifyContent="center"
                        width="28px"
                        height="28px"
                        borderRadius="6px"
                        backgroundColor="rgba(0,0,0,0.5)"
                        style={{ cursor: 'grab' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <LuGripVertical style={{ width: 14, height: 14, color: '#FFFFFF' }} />
                      </Flex>
                      {renderCard(cat)}
                    </Box>
                  )}
                </SortableRow>
              ))}
              {onAddCategory && (
                <Box
                  width="calc(50% - 6px)"
                  borderRadius="10px"
                  overflow="hidden"
                  cursor="pointer"
                  borderWidth="2px"
                  borderStyle="dashed"
                  borderColor={mt.border_color}
                  _hover={{ borderColor: mt.secondary_text_color }}
                  onClick={onAddCategory}
                >
                  <Box width="100%" height="0" paddingBottom="100%" position="relative">
                    <Flex position="absolute" inset="0" direction="column" justifyContent="center" alignItems="center" gap="8px" color={mt.secondary_text_color}>
                      <LuPlus size={24} />
                    </Flex>
                  </Box>
                  <Box p="10px">
                    <Box fontSize="14px" fontWeight="600" color={mt.secondary_text_color}>
                      {t('menus.addCategory')}
                    </Box>
                  </Box>
                </Box>
              )}
            </Flex>
          </SortableContext>
        </DndContext>
      </Box>
    );
  }

  return (
    <Box px="20px" py="16px">
      <Flex gap="12px" flexWrap="wrap">
        {categories.map((cat) => (
          <Box
            key={cat.id}
            width="calc(50% - 6px)"
            borderRadius="10px"
            overflow="hidden"
            cursor="pointer"
            transition="all 0.15s"
            _hover={{ opacity: 0.85 }}
            onClick={() => onCategorySelect(cat.id)}
          >
            {renderCard(cat)}
          </Box>
        ))}
      </Flex>
    </Box>
  );
};

export default CategoryGrid;
